// Website Pixels — list + detail drawer, fully wired to the API's pixel
// registry: reads via usePixels()/usePixelActivity(), writes via
// POST /pixels, PATCH /pixels/{id}, POST /pixels/{id}/archive,
// POST /pixels/{id}/duplicate, and GET /pixels/export.toml.
//
// Event vocabulary is the wire one (`Pageview` / `Purchase` — see
// shared::WebEventType); the prototype's AddToCart never existed in the
// pipeline. Activity counts come from `web_events` (PostgreSQL's recent days
// plus the day-sealed lake) through the batch tunnel (GET /pixels/activity), so a silent pixel is visible here.
//
// The drawer, the Studio, the modals and the pure model live in their own
// files: pixel-drawer, pixel-studio, pixel-new-modal, pixel-defaults-drawer,
// pixel-parts, and lib/pixel-draft + lib/pixel-studio-model.

import { ResponsiveKpis } from '@/components/responsive-kpis';
import { ResponsiveFilters } from '@/components/responsive-filters';
import { useEffect, useMemo, useState } from 'react';
import { pixelsList } from '@/api/resources';
import { Icons, fmt } from '@/components/primitives';
import { Dropdown } from '@/components/dropdowns';
import { createDataTable } from '@/components/data-table';
import type { VtColumn } from '@/components/data-table';
import { usePixels, usePixelActivity, usePixelDefaults } from '@/api/hooks';
import { api, networkOnlyRead } from '@/api/client';
import { useRegisteredMutation } from '@/api/mutation';
import { queryClient } from '@/cache/queryClient';
import { useCan } from '@/auth/capabilities';
import { MutationErrorLine, QueryErrorNote } from '@/components/error-panel';
import { triggerDownload } from '@/lib/csv';
import { fmtDay } from '@/lib/datetime';
import { PageActions } from '@/components/page-actions';
import {
  PIXEL_EVENTS,
  buildPixelPatch,
  defaultsFieldError,
  domainLabel,
  isValidPixelPath,
  normalizeDomain,
  pixelCoverage,
  pixelDraftRequiredIssues,
} from '@/lib/pixel-draft';
import type { NewPixelBody, PixelActivityByEvent, PixelDraft, PixelPatch } from '@/lib/pixel-draft';
import type { PixelApiValue, PixelResponse } from '@/api/types';
import { CoveragePills } from './pixel-parts';
import { PixelDetailDrawer } from './pixel-drawer';
import { NewPixelModal } from './pixel-new-modal';
import { OrgDefaultsDrawer } from './pixel-defaults-drawer';

// Prefix-invalidate every `pixels.list` variant after a write. Read off the
// resource: a rename must not silently turn this into a no-op.
const PIXELS_LIST_PREFIX = pixelsList.all;

// The event columns are generated from PIXEL_EVENTS, so the grid template, the
// headers and the empty-state colSpan all widen together. They used to be three
// independent facts — `vtCols` had ten hardcoded widths while the header ran a
// `.map()` — so adding a third tracked event would silently have mis-shaped the
// whole table.
const ROW_H = 52;
const COLUMNS: VtColumn[] = [
  { key: 'id',       header: 'ID',               width: 180, mobile: 'subtitle' },
  { key: 'desc',     header: 'Description',      width: 280, mobile: 'title' },
  ...PIXEL_EVENTS.map((ev): VtColumn => ({
    key: `event-${ev}`, header: ev, width: 120, title: `${ev} events in last 24h`, priority: 3,
  })),
  { key: 'coverage', header: 'Coverage',         width: 220, mobile: 'status' },
  { key: 'domains',  header: 'Domains',          width: 220, priority: 2, mobile: 'stat' },
  { key: 'funnel',   header: 'Funnel defaults',  width: 240, priority: 3 },
  { key: 'owner',    header: 'Owner',            width: 200, priority: 3 },
  { key: 'deployed', header: 'Deployed',         width: 120, priority: 3 },
  { key: 'events24', header: 'Events / 24h',     width: 130, align: 'num', mobile: 'stat' },
];
const { Table } = createDataTable(COLUMNS);

type StatusFilter = 'all' | 'active' | 'archived';
type CoverageFilter = 'all' | 'dual' | 'meta_only' | 'ttd_only';

/**
 * The open drawer: the pixel's id, and the row to show until the list holds it.
 * A row click and a write's response carry the row (instant open); a `?focus=`
 * deep link carries only the id, so its drawer waits for the list and never
 * opens on a stub.
 *
 * `savedAt` is when a save returned `snapshot` (0 otherwise): until the list
 * has refetched after that instant, the save's response is newer than the list
 * row, so the drawer shows it — the Users drawer's rule (U7). `notice` is the
 * sentence saying what the last save did; the drawer stays open after a save.
 */
interface Selection {
  id: string;
  snapshot: PixelApiValue | null;
  savedAt: number;
  notice: string | null;
}

export interface PixelsViewProps {
  setView?: (viewId: string) => void;
  focus?: string;
}

function PixelsView({ setView: _setView, focus }: PixelsViewProps) {
  const pixelsQuery = usePixels();
  const pixels = pixelsQuery.data ?? [];
  const defaultsQ = usePixelDefaults();
  const activityQuery = usePixelActivity();
  const canManage = useCan('pixels_manage', 'full');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [coverageFilter, setCoverageFilter] = useState<CoverageFilter>('all');
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [q, setQ] = useState('');
  // Selection holds a snapshot for instant open; the drawer re-derives the
  // fresh row from the list after invalidation (see `selected` below).
  const [selection, setSelection] = useState<Selection | null>(null);
  const select = (pixel: PixelApiValue | null) =>
    setSelection(pixel ? { id: pixel.id, snapshot: pixel, savedAt: 0, notice: null } : null);
  // A save's outcome lands on the drawer only if that pixel's drawer is still
  // open: a late success must not reopen a drawer the operator dismissed.
  const showSaved = (pixel: PixelApiValue, notice: string) =>
    setSelection(cur => cur?.id === pixel.id ? { id: pixel.id, snapshot: pixel, savedAt: Date.now(), notice } : cur);
  // The sentence goes as soon as the operator edits again, or saves again.
  const clearNotice = () => setSelection(cur => cur?.notice ? { ...cur, notice: null } : cur);
  const [newOpen, setNewOpen] = useState(false);
  const [defaultsOpen, setDefaultsOpen] = useState(false);
  const listRow = selection ? pixels.find(p => p.id === selection.id) : undefined;
  const listIsNewer = !!selection && pixelsQuery.dataUpdatedAt > selection.savedAt;
  const selected = selection
    ? ((listIsNewer ? listRow : undefined) ?? selection.snapshot ?? listRow ?? null)
    : null;
  // Deep-link ?focus=<id> opens that pixel's drawer once the list holds it. An
  // id the loaded list does not hold opens nothing.
  useEffect(() => { if (focus) setSelection({ id: focus, snapshot: null, savedAt: 0, notice: null }); }, [focus]);

  // { [pixel_id]: { [event_type]: PixelActivityRow } } — powers the per-event
  // columns, the Events/24h column, and the drawer Activity panel.
  const activityRows = activityQuery.data ?? [];
  const activityIssue = activityQuery.isError;
  const activityByPixel = useMemo(() => {
    const m: Record<string, PixelActivityByEvent> = {};
    for (const r of activityRows) (m[r.pixel_id] ||= {})[r.event_type] = r;
    return m;
  }, [activityRows]);
  const totalEvents24 = useMemo(
    () => activityRows.reduce((a, r) => a + (Number(r.count_24h) || 0), 0),
    [activityRows],
  );

  const invalidatePixels = () => queryClient.invalidateQueries({ queryKey: PIXELS_LIST_PREFIX });
  const patchM = useRegisteredMutation({
    domains: ['pixels'],
    mutationFn: ({ id, patch }: { id: string; patch: PixelPatch }) =>
      api.patch<PixelResponse>(`/pixels/${encodeURIComponent(id)}`, { body: patch }),
    onSuccess: () => { invalidatePixels(); },
  });
  const createM = useRegisteredMutation({
    domains: ['pixels'],
    mutationFn: (body: NewPixelBody) => api.post<PixelResponse>('/pixels', { body }),
    onSuccess: (resp) => { invalidatePixels(); setNewOpen(false); select(resp.pixel); },
  });
  const archiveM = useRegisteredMutation({
    domains: ['pixels'],
    mutationFn: (p: PixelApiValue) => p.status === 'active'
      ? api.post<PixelResponse>(`/pixels/${encodeURIComponent(p.id)}/archive`)
      : api.patch<PixelResponse>(`/pixels/${encodeURIComponent(p.id)}`, { body: { status: 'active' } }),
    onSuccess: (resp) => { invalidatePixels(); select(resp.pixel); },
  });
  const dupM = useRegisteredMutation({
    domains: ['pixels'],
    mutationFn: ({ id, newId }: { id: string; newId: string }) =>
      api.post<PixelResponse>(`/pixels/${encodeURIComponent(id)}/duplicate`, { body: { new_id: newId } }),
    onSuccess: (resp) => { invalidatePixels(); select(resp.pixel); },
  });
  // A stale error from the last pixel shouldn't greet the next one.
  useEffect(() => { patchM.reset(); archiveM.reset(); dupM.reset(); }, [selection?.id]);

  // Saving keeps the drawer open on the pixel the server returned, with
  // "Saved." at its top; closing stays with ×, Escape or a click outside.
  const onSave = (before: PixelApiValue, draft: PixelDraft) => {
    clearNotice();
    const patch = buildPixelPatch(before, draft);
    if (!Object.keys(patch).length) {
      setSelection(cur => cur?.id === before.id ? { ...cur, notice: 'No changes to save.' } : cur);
      return;
    }
    patchM.mutate({ id: before.id, patch }, {
      onSuccess: (resp) => showSaved(resp.pixel, 'Saved.'),
    });
  };

  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState<unknown>(null);
  const exportToml = async () => {
    setExportBusy(true);
    setExportError(null);
    try {
      const text = await networkOnlyRead<string>('/pixels/export.toml');
      triggerDownload('pixels-export.toml', new Blob([text], { type: 'application/toml;charset=utf-8' }));
    } catch (e) {
      setExportError(e);
    } finally {
      setExportBusy(false);
    }
  };

  const rows = pixels.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (q.trim()) {
      const needle = q.toLowerCase();
      if (!(p.id + ' ' + (p.description||'') + ' ' + (p.owner_email||'')).toLowerCase().includes(needle)) return false;
    }
    const cov = pixelCoverage(p);
    if (coverageFilter === 'meta_only' && !(cov.meta && !cov.ttd)) return false;
    if (coverageFilter === 'ttd_only' && !(cov.ttd && !cov.meta)) return false;
    if (coverageFilter === 'dual' && !(cov.meta && cov.ttd)) return false;
    if (domainFilter !== 'all' && !(p.domains || []).includes(domainFilter)) return false;
    return true;
  });

  const activeCount = pixels.filter(p => p.status === 'active').length;
  const archivedCount = pixels.filter(p => p.status === 'archived').length;
  const dualCount = pixels.filter(p => { const c=pixelCoverage(p); return c.meta && c.ttd; }).length;

  // Distinct domains across all pixels, sorted alphabetically — drives the
  // domain filter dropdown so users can scope the table to a single site.
  const allDomains = useMemo(
    () => [...new Set(pixels.flatMap(p => p.domains || []))].sort(),
    [pixels]
  );


  return (
    <div className="tbl-frame">
      <div className="page-hd">
        <div>
          <h1 className="page-title">Website Pixels</h1>
        </div>
        <PageActions>
          {canManage ? (
            <button
              className="btn btn-sm btn-ghost mono"
              style={{fontSize:10, letterSpacing:'.06em'}}
              disabled={defaultsQ.isLoading || defaultsQ.isError}
              title={defaultsQ.isError ? 'Organization defaults could not be read' : 'Edit the identifiers that prefill new pixels'}
              onClick={() => setDefaultsOpen(true)}
            >
              Org defaults · {defaultsQ.isLoading ? 'loading' : defaultsQ.isError ? 'unavailable' : defaultsQ.data?.configured ? 'configured' : 'not configured'}
            </button>
          ) : (
            <span className="muted mono" data-phone="hidden" style={{fontSize:10, letterSpacing:'.06em'}}>
              Org defaults · {defaultsQ.isLoading ? 'loading' : defaultsQ.isError ? 'unavailable' : defaultsQ.data?.configured ? 'configured' : 'not configured'}
            </span>
          )}
          <button className="btn" disabled={exportBusy} onClick={exportToml}><span>{Icons.dl}</span>{exportBusy ? 'Exporting…' : 'Export TOML'}</button>
          {canManage && (
            <button className="btn btn-primary" data-phone="inline" disabled={defaultsQ.isLoading} title={defaultsQ.isLoading ? 'Waiting for organization defaults' : undefined} onClick={()=>setNewOpen(true)}><span>{Icons.plus}</span><span className="phone-long">New pixel</span><span className="phone-short">New</span></button>
          )}
        </PageActions>
      </div>

      {exportError ? (
        <div style={{padding:'8px 22px', borderBottom:'1px solid var(--line)'}}>
          <MutationErrorLine error={exportError}/>
        </div>
      ) : null}

      {activityQuery.isError && (
        <div style={{padding:'8px 22px', borderBottom:'1px solid var(--line)'}}>
          <QueryErrorNote
            error={activityQuery.error}
            what="pixel activity"
            onRetry={() => void activityQuery.refetch()}
          />
        </div>
      )}
      {defaultsQ.isError && (
        <div style={{padding:'8px 22px', borderBottom:'1px solid var(--line)'}}>
          <QueryErrorNote
            error={defaultsQ.error}
            what="organization pixel defaults"
            onRetry={() => void defaultsQ.refetch()}
          />
        </div>
      )}

      <ResponsiveKpis><div className="kpi-strip">
        {[
          ['Active',          activeCount],
          ['Archived',        archivedCount],
          ['Dual coverage',   dualCount],
          ['Events / 24h',    activityIssue ? '—' : fmt.n(totalEvents24)],
          ['Domains covered', new Set(pixels.flatMap(p => p.domains || [])).size],
        ].map(([k,v]) => (
          <div className="kpi" key={k}>
            <div className="kpi-label">{k}</div>
            <div className="kpi-val">{v}</div>
          </div>
        ))}
      </div></ResponsiveKpis>

      <ResponsiveFilters className="filter-bar">
        <div className="chip-input" style={{flex:'1 1 0'}}>
          <span>{Icons.search}</span>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Filter pixels by ID, description, or owner"/>
        </div>
        <Dropdown value={statusFilter} data-filter-default="active" options={[
          { id:'all', label:'All statuses' },
          { id:'active', label:'Active' },
          { id:'archived', label:'Archived' },
        ]} onChange={setStatusFilter} label="Status"/>
        <Dropdown value={coverageFilter} options={[
          { id:'all', label:'All coverage' },
          { id:'dual', label:'Meta + CTV/Display' },
          { id:'meta_only', label:'Meta only' },
          { id:'ttd_only', label:'CTV/Display only' },
        ]} onChange={setCoverageFilter} label="Coverage"/>
        <Dropdown value={domainFilter} options={[
          { id:'all', label:'All domains' },
          ...allDomains.map(d => ({ id: d, label: domainLabel(d) })),
        ]} onChange={setDomainFilter} label="Domain" width={200}/>
      </ResponsiveFilters>

      <Table
        rows={rows}
        rowH={ROW_H}
        unfilteredTotal={pixels.length}
        deps={[{ what: 'pixels', query: pixelsQuery }]}
        empty="No pixels yet. Create one to start tracking conversions."
        noMatch="No pixels match these filters."
        onRowClick={select}
        renderRow={(p) => {
          const cov = pixelCoverage(p);
          const hasFunnel = !!(p.default_venue_id || p.default_production_id);
          const act = activityByPixel[p.id];
          const total24 = PIXEL_EVENTS.reduce((a, ev) => a + (act?.[ev]?.count_24h ?? 0), 0);
          return (
            <>
              <td className="name mono" style={{fontSize:11.5,letterSpacing:'.02em'}}>{p.id}</td>
              <td style={{lineHeight:1.3}}>{p.description}</td>
              {PIXEL_EVENTS.map(ev => {
                const c = act?.[ev]?.count_24h ?? 0;
                const live = !activityIssue && c > 0;
                return (
                  <td key={ev}>
                    <span style={{display:'inline-flex',alignItems:'center',gap:6,fontFamily:'var(--font-mono)',fontSize:11,fontVariantNumeric:'tabular-nums',color: live ? 'var(--ink-1)' : 'var(--ink-3)'}}>
                      <span style={{width:7,height:7,borderRadius:'50%',background: live ? 'var(--good)' : 'var(--ink-3)',opacity: live ? 1 : .45,flexShrink:0}}/>
                      {activityIssue
                        ? <span className="muted">—</span>
                        : (c > 0 ? fmt.n(c) : <span className="muted">0</span>)}
                    </span>
                  </td>
                );
              })}
              <td><CoveragePills pixel={p}/></td>
              <td>
                <span title={(p.domains||[]).map(domainLabel).join('\n')} style={{cursor:'help',overflow:'hidden',textOverflow:'ellipsis'}}>
                  {(p.domains||[])[0] ? (domainLabel(p.domains[0]).slice(0,28) + (domainLabel(p.domains[0]).length>28?'…':'')) : '—'}
                  {(p.domains||[]).length > 1 && <span className="muted mono" style={{fontSize:10,marginLeft:6}}>+{p.domains.length-1}</span>}
                </span>
              </td>
              <td>
                {hasFunnel
                  ? <span className="mono" style={{fontSize:11,overflow:'hidden',textOverflow:'ellipsis'}}>{p.default_venue_id}{p.default_production_id ? ' · ' + p.default_production_id : ''}</span>
                  : <span className="muted" style={{fontStyle:'italic',fontSize:11}}>Page-supplied</span>}
              </td>
              <td className="muted mono" style={{fontSize:11}}>{p.owner_email}</td>
              <td className="mono" style={{fontSize:11}}>{fmtDay(p.deployed_at)}</td>
              <td className="num">
                {activityIssue
                  ? <span className="dim">—</span>
                  : <span className="mono" style={{fontWeight: total24 > 0 ? 600 : 400, color: total24 > 0 ? 'var(--ink-1)' : 'var(--ink-3)'}}>{fmt.n(total24)}</span>}
              </td>
            </>
          );
        }}
      />

      {selected && (
        <PixelDetailDrawer
          pixel={selected}
          activity={activityByPixel[selected.id]}
          activityIssue={activityIssue}
          onClose={()=>select(null)}
          onSave={onSave}
          notice={selection?.notice ?? null}
          savedAt={selection?.savedAt ?? 0}
          onEdit={clearNotice}
          onArchive={(p)=>archiveM.mutate(p)}
          onDuplicate={(id, newId)=>dupM.mutate({ id, newId })}
          busy={patchM.isPending || archiveM.isPending || dupM.isPending}
          actionError={patchM.error || archiveM.error || dupM.error}
          canManage={canManage}
          providerDefaults={defaultsQ.data}
        />
      )}
      <NewPixelModal
        open={newOpen}
        onClose={()=>setNewOpen(false)}
        onCreate={(body)=>createM.mutate(body)}
        submitting={createM.isPending}
        error={createM.error}
        onResetError={()=>createM.reset()}
        defaults={defaultsQ.data}
      />
      {defaultsOpen && canManage && (
        <OrgDefaultsDrawer defaults={defaultsQ.data} onClose={()=>setDefaultsOpen(false)}/>
      )}
    </div>
  );
}

export {
  PixelsView,
  PixelDetailDrawer,
  NewPixelModal,
  CoveragePills,
  normalizeDomain,
  isValidPixelPath,
  pixelDraftRequiredIssues,
  defaultsFieldError,
};
